import { useId, type ChangeEvent, type InputHTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { useT, type Replacements } from '../../i18n';
import { cx } from './classNames';
import { FormattedInput } from './FormattedInput';
import styles from './FormField.module.css';

interface FormFieldBaseProps {
  id?: string;
  labelKey: string;
  /**
   * R3-03: Platzhalter der Beschriftung (z.B. `{n}` in „Eigenheit {n}").
   * Ein Key MIT Parameter statt eigener Keys je Zeile — sonst waechst die
   * 75-Sprachen-Tabelle mit jeder zusaetzlichen Formularzeile mit.
   */
  labelParams?: Replacements;
  helpText?: ReactNode;
  helpKey?: string;
  errorText?: ReactNode;
  errorKey?: string;
  className?: string;
  /**
   * Optionale Aktion rechts neben der Beschriftung (R2-06: der Wuerfel im
   * Begleiter-Bogen). Bewusst in der Label-Zeile und NICHT im Feld: eine
   * ueberlagerte Schaltflaeche verdeckt bei mehrzeiligen Feldern und im
   * FormattedInput (contenteditable) den Text und kollidiert mit dem
   * 44-px-Trefferflaechen-Ratchet.
   *
   * R3-03: Fuer EINZEILIGE, nicht formatierte Felder gibt es zusaetzlich
   * `trailingAction` — die Aktion sitzt dann IM Feld (Julius' Wunsch). Die
   * Begruendung oben bleibt fuer alle anderen Faelle gueltig: Textareas und
   * FormattedInput behalten ausschliesslich `labelAction`.
   */
  labelAction?: ReactNode;
  /**
   * R4-01: sichtbarer Zeichenzaehler unter dem Feld („{n}/{max}"). Bewusst
   * NICHT ueber `aria-describedby` mit dem Feld verbunden — sonst liest ein
   * Screenreader bei JEDEM Tastendruck die Zahl mit vor. Der Deckel selbst
   * steht am Feld (`maxLength`).
   */
  counter?: { value: number; max: number };
}

interface CustomFormFieldProps extends FormFieldBaseProps {
  children: ReactNode;
  as?: undefined;
}

interface InputFormFieldProps extends FormFieldBaseProps, Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className' | 'children'> {
  as?: 'input';
  children?: never;
  /** Inline-Markdown-Formatierung (*fett* _kursiv_ …) im Feld. Opt-in für
   *  einzeilige Text-Felder (nur type text/undefined). Default: aus. */
  formatted?: boolean;
  /**
   * R3-03: Aktion INNERHALB des Feldes, am Zeilenende (der Wuerfel des
   * Begleiter-Bogens). Bewusst NUR hier deklariert — das Typsystem verhindert
   * damit den Einsatz an `textarea`/`select`, wo eine ueberlagerte
   * Schaltflaeche den Text verdecken wuerde. Ist zusaetzlich `formatted`
   * gesetzt (contenteditable, nowrap + overflow-x), wird `trailingAction`
   * defensiv ignoriert.
   */
  trailingAction?: ReactNode;
  /**
   * R4-01: mitwachsendes Feld. Statt eines <input> wird ein FormattedInput
   * (contenteditable, `multiline={false} wrap`) gerendert — das Feld waechst
   * mit dem Text, sein WERT bleibt aber garantiert einzeilig.
   *
   * Bewusst NUR an `input` deklariert: die Begruendung oben (eine ueberlagerte
   * Schaltflaeche verdeckt mehrzeiligen Text) gilt fuer echte Prosa-Felder
   * unveraendert weiter. Hier ist sie geloest, weil `trailingAction` bei
   * `autoGrow` auf die HOEHE DER ERSTEN ZEILE gepinnt wird (Klasse
   * `controlWrapGrowing`) und der gesamte Textblock rechts Platz laesst.
   */
  autoGrow?: boolean;
  /**
   * R4-01: Enter im mitwachsenden Feld. FormattedInput wehrt den Umbruch mit
   * preventDefault() ab; ein Formular-Handler weiter oben steigt danach aus.
   * Nur mit `autoGrow`/`formatted` wirksam.
   */
  onEnter?: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
}

interface TextareaFormFieldProps extends FormFieldBaseProps, Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'className' | 'children'> {
  as: 'textarea';
  children?: never;
  /** Inline-Markdown-Formatierung im Feld. Default: AN (Prosa-Feld). Mit
   *  formatted={false} abschaltbar (z.B. reine Code-/JSON-Eingaben). */
  formatted?: boolean;
}

interface SelectFormFieldProps extends FormFieldBaseProps, Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'className'> {
  as: 'select';
  children: ReactNode;
}

export type FormFieldProps = CustomFormFieldProps | InputFormFieldProps | TextareaFormFieldProps | SelectFormFieldProps;

function stripControlProps<
  T extends {
    as?: unknown;
    children?: unknown;
    formatted?: unknown;
    trailingAction?: unknown;
    autoGrow?: unknown;
    onEnter?: unknown;
  },
>(props: T): Omit<T, 'as' | 'children' | 'formatted' | 'trailingAction' | 'autoGrow' | 'onEnter'> {
  const controlProps = { ...props };
  delete controlProps.as;
  delete controlProps.children;
  delete controlProps.formatted; // nur Steuer-Flag, kein DOM-Attribut
  delete controlProps.trailingAction; // R3-03: Render-Slot, kein DOM-Attribut
  delete controlProps.autoGrow; // R4-01: Steuer-Flag, kein DOM-Attribut
  delete controlProps.onEnter; // R4-01: Callback, kein DOM-Attribut
  return controlProps as Omit<T, 'as' | 'children' | 'formatted' | 'trailingAction' | 'autoGrow' | 'onEnter'>;
}

// Adapter: FormattedInput liefert onChange(value:string); die FormField-Consumer
// erwarten ein Textarea/Input-Event mit event.target.value. Wir synthetisieren
// ein minimales Event (alle Textarea-Consumer lesen ausschließlich .target.value).
function makeSyntheticChange(
  onChange: ((event: ChangeEvent<HTMLTextAreaElement>) => void) | undefined,
  id: string,
): (value: string) => void {
  return (value: string) => {
    if (!onChange) return;
    onChange({ target: { value, id }, currentTarget: { value, id } } as unknown as ChangeEvent<HTMLTextAreaElement>);
  };
}

export function FormField(props: FormFieldProps) {
  const { t } = useT();
  const generatedId = useId();
  const {
    id,
    labelKey,
    helpText,
    helpKey,
    errorText,
    errorKey,
    className,
    labelAction,
    labelParams,
    counter,
    ...controlProps
  } = props;
  const fieldId = id ?? generatedId;
  const helpId = `${fieldId}-help`;
  const errorId = `${fieldId}-error`;
  const help = helpKey ? t(helpKey) : helpText;
  const error = errorKey ? t(errorKey) : errorText;
  const describedBy = [help ? helpId : undefined, error ? errorId : undefined].filter(Boolean).join(' ') || undefined;
  const labelText = t(labelKey, labelParams);

  let field: ReactNode;
  if (props.as === 'textarea') {
    const textareaProps = stripControlProps(controlProps) as Omit<
      TextareaHTMLAttributes<HTMLTextAreaElement>,
      'id' | 'className' | 'children'
    >;
    // Inline-Markdown standardmäßig AN für Prosa-Textareas, sofern kontrolliert
    // (value:string + onChange). Sonst native Textarea (Fallback).
    const controlled = typeof textareaProps.value === 'string' && typeof textareaProps.onChange === 'function';
    if (props.formatted !== false && controlled) {
      field = (
        <FormattedInput
          id={fieldId}
          className={styles.control}
          value={textareaProps.value as string}
          onChange={makeSyntheticChange(textareaProps.onChange as (e: ChangeEvent<HTMLTextAreaElement>) => void, fieldId)}
          placeholder={typeof textareaProps.placeholder === 'string' ? textareaProps.placeholder : undefined}
          maxLength={typeof textareaProps.maxLength === 'number' ? textareaProps.maxLength : undefined}
          disabled={Boolean(textareaProps.disabled)}
          rows={typeof textareaProps.rows === 'number' ? textareaProps.rows : undefined}
          ariaLabel={labelText}
          multiline
        />
      );
    } else {
      field = (
        <textarea
          {...textareaProps}
          id={fieldId}
          className={styles.control}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
        />
      );
    }
  } else if (props.as === 'select') {
    const selectProps = stripControlProps(controlProps) as Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'className'>;
    field = (
      <select
        {...selectProps}
        id={fieldId}
        className={styles.control}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
      >
        {props.children}
      </select>
    );
  } else if (props.as === 'input') {
    const inputProps = stripControlProps(controlProps) as Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className' | 'children'>;
    const isTextType = !inputProps.type || inputProps.type === 'text' || inputProps.type === 'search';
    const controlled = typeof inputProps.value === 'string' && typeof inputProps.onChange === 'function';
    // Einzeilige Felder: Inline-Markdown nur per Opt-in (formatted) + nur für
    // Text-Typen (nicht number/email/date/…) und kontrollierte Felder.
    if (props.formatted && isTextType && controlled) {
      field = (
        <FormattedInput
          id={fieldId}
          className={styles.control}
          value={inputProps.value as string}
          onChange={makeSyntheticChange(inputProps.onChange as unknown as (e: ChangeEvent<HTMLTextAreaElement>) => void, fieldId)}
          placeholder={typeof inputProps.placeholder === 'string' ? inputProps.placeholder : undefined}
          maxLength={typeof inputProps.maxLength === 'number' ? inputProps.maxLength : undefined}
          disabled={Boolean(inputProps.disabled)}
          ariaLabel={labelText}
          multiline={false}
          onEnter={props.onEnter}
        />
      );
    } else if (props.autoGrow && isTextType && controlled) {
      // R4-01: mitwachsendes, im Wert einzeiliges Feld. Der Baum ist derselbe
      // wie im <input>-Zweig — `trailingAction` bleibt ein GESCHWISTER, die
      // Beschriftung (htmlFor/id) und aria-describedby bleiben verbunden.
      const trailingAction = props.trailingAction;
      const grown = (
        <FormattedInput
          id={fieldId}
          className={cx(styles.control, trailingAction ? styles.controlWithAction : undefined)}
          value={inputProps.value as string}
          onChange={makeSyntheticChange(inputProps.onChange as unknown as (e: ChangeEvent<HTMLTextAreaElement>) => void, fieldId)}
          placeholder={typeof inputProps.placeholder === 'string' ? inputProps.placeholder : undefined}
          maxLength={typeof inputProps.maxLength === 'number' ? inputProps.maxLength : undefined}
          disabled={Boolean(inputProps.disabled)}
          ariaLabel={labelText}
          ariaDescribedBy={describedBy}
          ariaInvalid={Boolean(error)}
          rows={1}
          multiline={false}
          wrap
          onEnter={props.onEnter}
        />
      );
      field = trailingAction ? (
        <div className={cx(styles.controlWrap, styles.controlWrapGrowing)}>
          {grown}
          <span className={styles.trailingAction}>{trailingAction}</span>
        </div>
      ) : grown;
    } else {
      // R3-03: Die Aktion ist ein GESCHWISTER des <input>, kein Kind — der
      // Wert, die Beschriftung (htmlFor/id) und aria-describedby bleiben
      // unveraendert. Ohne `trailingAction` wird der Wrapper gar nicht erst
      // gerendert (Bestandsfelder behalten exakt ihren bisherigen Baum).
      const trailingAction = props.trailingAction;
      const input = (
        <input
          {...inputProps}
          id={fieldId}
          className={cx(styles.control, trailingAction ? styles.controlWithAction : undefined)}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
        />
      );
      field = trailingAction ? (
        <div className={styles.controlWrap}>
          {input}
          <span className={styles.trailingAction}>{trailingAction}</span>
        </div>
      ) : input;
    }
  } else {
    field = props.children;
  }

  return (
    <div className={cx(styles.field, className)}>
      {labelAction ? (
        <div className={styles.labelRow}>
          <label className={styles.label} htmlFor={fieldId}>
            {labelText}
          </label>
          {labelAction}
        </div>
      ) : (
        <label className={styles.label} htmlFor={fieldId}>
          {labelText}
        </label>
      )}
      {field}
      {counter ? (
        // Dauerhaft sichtbar (nicht erst kurz vor dem Deckel): eine Grenze, die
        // sich erst bei 160 von 200 Zeichen zeigt, erklaert sich genau dann
        // nicht, wenn der Nutzer sie noch einplanen koennte.
        <div
          className={styles.counter}
          title={t('form.char_counter_label', { n: counter.value, max: counter.max })}
        >
          {t('form.char_counter', { n: counter.value, max: counter.max })}
        </div>
      ) : null}
      {help ? (
        <div className={styles.help} id={helpId}>
          {help}
        </div>
      ) : null}
      {error ? (
        <div className={styles.error} id={errorId}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
